import React, { useState } from "react";
import { QrCode, CheckCircle2, AlertCircle, X, Search, ShieldCheck } from "lucide-react";
import { apiClient } from "@/lib/api-client";
import { toast } from "sonner";

export default function VolunteerCheckInModal({ isOpen, onClose, shifts = [], onRefresh }) {
  const [tokenInput, setTokenInput] = useState("");
  const [statusMsg, setStatusMsg] = useState(null);

  if (!isOpen) return null;

  const handleCheckInToken = async (qrToken) => {
    try {
      const res = await apiClient.post("/events/check-in", { qr_token: qrToken });
      setStatusMsg({ type: "success", text: `Check-in successful! Volunteer: ${res.data.volunteer_name}` });
      toast.success(`Checked in ${res.data.volunteer_name}! (+4.0 hrs credit logged)`);
      if (onRefresh) onRefresh();
    } catch (err) {
      const msg = err?.response?.data?.message || "Check-in failed. Invalid or used QR token.";
      setStatusMsg({ type: "error", text: msg });
      toast.error(msg);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl space-y-5 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
        >
          <X className="w-5 h-5" />
        </button>

        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <QrCode className="w-6 h-6 text-indigo-600" />
            On-Site Volunteer Check-In
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Scan QR token or manually verify volunteer waiver status and log service hours.
          </p>
        </div>

        {/* Manual QR Token Input */}
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
          <label className="text-xs font-semibold text-slate-700 block">
            Scan / Enter QR Token Code
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="e.g. QR-1722500000000-1234"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              className="flex-1 px-3.5 py-2 text-xs bg-white border border-slate-200 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
            <button
              onClick={() => handleCheckInToken(tokenInput)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg shadow-sm"
            >
              Check-In
            </button>
          </div>
        </div>

        {statusMsg && (
          <div
            className={`p-3 rounded-lg text-xs flex items-center gap-2 ${
              statusMsg.type === "success"
                ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                : "bg-red-50 text-red-800 border border-red-200"
            }`}
          >
            {statusMsg.type === "success" ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
            )}
            <span>{statusMsg.text}</span>
          </div>
        )}

        {/* Registered Shifts Table for Manual Verification */}
        <div className="space-y-2">
          <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Registered Shift Roster ({shifts.length})
          </h3>
          <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
            {shifts.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400">No shifts registered yet.</div>
            ) : (
              shifts.map((s) => (
                <div key={s._id} className="p-3 flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-semibold text-slate-900">{s.volunteer_name}</h4>
                    <span className="text-[11px] text-slate-500">{s.volunteer_email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.waiver_signed && (
                      <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-md font-medium border border-emerald-200">
                        <ShieldCheck className="w-3 h-3" /> Waiver Signed
                      </span>
                    )}
                    {s.status === "CHECKED_IN" ? (
                      <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                        Checked In
                      </span>
                    ) : (
                      <button
                        onClick={() => handleCheckInToken(s.qr_token)}
                        className="text-[11px] font-semibold bg-slate-100 hover:bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-lg border border-slate-200"
                      >
                        Check In
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
